export type ColumnConfig = {
  orderedKeys: string[];
  hiddenKeys: string[];
};

const samePath = (a: string[], b: string[]) => a.length === b.length && a.every((name, i) => name === b[i]);

const lastIndexWhere = <T>(items: T[], predicate: (item: T) => boolean) => {
  for (let i = items.length - 1; i >= 0; i--) if (predicate(items[i])) return i;
  return -1;
};

const sharedDepth = (a: string[], b: string[]) => {
  const depth = a.findIndex((name, i) => b[i] !== name);
  return depth === -1 ? Math.min(a.length, b.length) : depth;
};

export const reconcileColumnKeys = (
  savedKeys: string[],
  allKeys: string[],
  groupsOf: (key: string) => string[] = () => [],
): string[] => {
  const allKeySet = new Set(allKeys);
  const result = savedKeys.filter((k) => allKeySet.has(k));
  const placed = new Set(result);
  allKeys.forEach((key, schemaIndex) => {
    if (placed.has(key)) return;
    const path = groupsOf(key);
    let at = -1;
    for (let depth = path.length; depth > 0; depth--) {
      const last = lastIndexWhere(result, (other) => sharedDepth(groupsOf(other), path) >= depth);
      if (last !== -1) {
        at = last + 1;
        break;
      }
    }
    if (at === -1) {
      const previous = lastIndexWhere(allKeys.slice(0, schemaIndex), (other) => placed.has(other));
      at = previous === -1 ? 0 : result.indexOf(allKeys[previous]) + 1;
    }
    result.splice(at, 0, key);
    placed.add(key);
  });
  return result;
};

export const loadStoredConfig = (storageKey: string): ColumnConfig => {
  try {
    const saved = localStorage.getItem(storageKey);
    if (!saved) return { orderedKeys: [], hiddenKeys: [] };
    const parsed = JSON.parse(saved);
    return {
      orderedKeys: Array.isArray(parsed.orderedKeys) ? parsed.orderedKeys : [],
      hiddenKeys: Array.isArray(parsed.hiddenKeys) ? parsed.hiddenKeys : [],
    };
  } catch {
    return { orderedKeys: [], hiddenKeys: [] };
  }
};

export type ColumnRun<C> = { group: string | null; columns: C[] };

export const columnRuns = <C>(columns: C[], groupOf: (column: C) => string | null | undefined): ColumnRun<C>[] =>
  columns.reduce<ColumnRun<C>[]>((runs, column) => {
    const group = groupOf(column) ?? null;
    const last = runs[runs.length - 1];
    if (last && group !== null && last.group === group) last.columns.push(column);
    else runs.push({ group, columns: [column] });
    return runs;
  }, []);

export type GroupedItem = { key: string; groups?: string[] };

export type ColumnNode = { key: string } | { groupPath: string[] };

export const isInGroup = (item: GroupedItem, path: string[]) =>
  path.every((name, depth) => item.groups?.[depth] === name);

const nodeRange = (items: GroupedItem[], node: ColumnNode): [number, number] => {
  if ("key" in node) {
    const index = items.findIndex((item) => item.key === node.key);
    return [index, index + 1];
  }
  const start = items.findIndex((item) => isInGroup(item, node.groupPath));
  const end = lastIndexWhere(items, (item) => isInGroup(item, node.groupPath)) + 1;
  return [start, end];
};

const nodeParent = (items: GroupedItem[], node: ColumnNode) =>
  "key" in node ? (items.find((item) => item.key === node.key)?.groups ?? []) : node.groupPath.slice(0, -1);

const nodeAtDepth = (item: GroupedItem, depth: number): ColumnNode =>
  (item.groups?.length ?? 0) > depth ? { groupPath: item.groups!.slice(0, depth + 1) } : { key: item.key };

export const moveColumnNode = <T extends GroupedItem>(
  items: T[],
  dragged: ColumnNode,
  overStart: number,
  overEnd: number = overStart,
): T[] => {
  const parent = nodeParent(items, dragged);
  const over = items[overStart];
  if (!over || !isInGroup(over, parent)) return items;
  const [from, fromEnd] = nodeRange(items, dragged);
  const [to, toEnd] = nodeRange(items, nodeAtDepth(over, parent.length));
  if (from === -1 || from === to) return items;
  const movingDown = from < to;
  if (movingDown ? overEnd !== toEnd - 1 : overStart !== to) return items;
  const block = items.slice(from, fromEnd);
  const rest = [...items.slice(0, from), ...items.slice(fromEnd)];
  const insertAt = movingDown ? toEnd - block.length : to;
  return [...rest.slice(0, insertAt), ...block, ...rest.slice(insertAt)];
};

export const groupPathKey = (path: string[]) => path.join("\u001f");

export const isSameNode = (a: ColumnNode | null, b: ColumnNode) =>
  a !== null && ("key" in a ? "key" in b && a.key === b.key : "groupPath" in b && samePath(a.groupPath, b.groupPath));

export const scaleColumnWidths = (
  startWidths: ReadonlyMap<string, number>,
  delta: number,
  minWidth: number,
): Map<string, number> => {
  const total = [...startWidths.values()].reduce((sum, width) => sum + width, 0);
  const scale = total ? Math.max(total + delta, 0) / total : 1;
  return new Map([...startWidths].map(([key, width]) => [key, Math.max(minWidth, Math.round(width * scale))]));
};
